// THE LONG KINDLING — world module (loadable via ?world=longkindling).
//
// === PER-ROOM FILE CONVENTION (so the 25-room fan-out adds files, never merges) ===
// Each room lives in its OWN file `src/rooms/longkindling/<room_id>.ts` that does exactly two things:
//   1. exports a plain authored data object `<roomNameCamel>` (the v3 script translated to schema data);
//   2. exports `room` = `loadRoom(<object>)` (the parsed+validated Room).
// `room_id` MUST be the GAME_MAP slug (hearth_hall, cottage, bakehouse, midwife, forge, ...).
//
// === SHARED GLOBAL FLAG REGISTRY (the single source of truth for cross-room gates) ===
// Every `setGlobal`/`toggleGlobal` key + every cross-room `gatedBy {scope:"global"}` key is declared
// here so gates line up across rooms and `analyzeQuest` (runs on every boot/test) can flag soft-locks.
//   has_lamp      (R1 hearth-hall keeper's lamp -> R2 lintel decode; R19/R20/R24 slant-reads)
//   third_bowl    (R2 cottage -> the extra place's bowl)                    [refusal economy]
//   sanding_stone (R2 cottage -> keeps any returning planed name blank)
//   has_blanket   (R2 cottage -> the good wool blanket, R17 refusal prop)
//   ate_crust     (R3 bakehouse -> the Hush sympathy-lean)
//   has_jacket    (R6 granary -> the unclaimed child's jacket, R10 kin-confirm)
//   tally_stick   (R7 green & well -> gates the edge R11 hedge-gap notch-board)
//   good_axe      (R5 forge -> R14 felling-front, the disciplined-advance reads)
//   has_garland   (R11 hedge-gap -> the thaw-calendar carried to R13)
//   maul          (R16 toolshed -> R13 last-stake re-drive)
//   carved_dog    (R16 toolshed -> the kin discovery)
//   rowan_sprigs  (R18 wood-path / R22 warding-stones -> folk-comfort deeper in; R21/R24 steadying)
//   round_decoded (R12 cleared-ground walk-the-round -> Act III wood gates + R24 undercroft reveal)
//   old_record    (R10 silent-women record read -> R24 undercroft reveal with round_decoded)
//   why_known     (R24 undercroft reveal -> R23 deep-wood limit + R25 hush-hearth social climax)
// REFUSE puzzles are reversible toggles (e.g. R1 `chair_warmed`, R12 short-cut) and are NEVER gate
// keys (their global decode-flags are set monotonically by separate one-way hotspots) — no soft-lock.
import type { Room } from "../../core/schema";
import type { QuestSpec } from "../../engine/quest";

// THE WARM — the village (Act I)
import { room as hearthHall } from "./hearth_hall";       // R1
import { room as cottage } from "./cottage";              // R2
import { room as bakehouse } from "./bakehouse";          // R3
import { room as midwife } from "./midwife";              // R4
import { room as forge } from "./forge";                  // R5
import { room as granary } from "./granary";              // R6
import { room as greenWell } from "./green_well";         // R7  (the hub)
import { room as longhouse } from "./longhouse";          // R8
import { room as wardingYard } from "./warding_yard";     // R9
import { room as silentWomen } from "./silent_women";     // R10
// THE EDGE — the line (Act II)
import { room as hedgeGap } from "./hedge_gap";           // R11
import { room as clearedGround } from "./cleared_ground"; // R12
import { room as lastStake } from "./last_stake";         // R13
import { room as fellingFront } from "./felling_front";   // R14
import { room as nightHut } from "./night_hut";           // R15
import { room as toolshed } from "./toolshed";            // R16
// THE WOOD & THE REFUSAL (Act III)
import { room as theEdgeDusk } from "./the_edge_dusk";    // R17  (the treeline / the Watch)
import { room as woodPath } from "./wood_path";           // R18
import { room as firstFelling } from "./first_felling";   // R19
import { room as foundersRuin } from "./founders_ruin";   // R20
import { room as theLow } from "./the_low";               // R21
import { room as wardingStones } from "./warding_stones"; // R22
import { room as deepWoodLimit } from "./deep_wood_limit";// R23
import { room as undercroft } from "./undercroft";        // R24
import { room as hushHearth } from "./hush_hearth";       // R25

// The room registry (keyed by GAME_MAP slug).
export const rooms: Record<string, Room> = {
  [hearthHall.id]: hearthHall,
  [cottage.id]: cottage,
  [bakehouse.id]: bakehouse,
  [midwife.id]: midwife,
  [forge.id]: forge,
  [granary.id]: granary,
  [greenWell.id]: greenWell,
  [longhouse.id]: longhouse,
  [wardingYard.id]: wardingYard,
  [silentWomen.id]: silentWomen,
  [hedgeGap.id]: hedgeGap,
  [clearedGround.id]: clearedGround,
  [lastStake.id]: lastStake,
  [fellingFront.id]: fellingFront,
  [nightHut.id]: nightHut,
  [toolshed.id]: toolshed,
  [theEdgeDusk.id]: theEdgeDusk,
  [woodPath.id]: woodPath,
  [firstFelling.id]: firstFelling,
  [foundersRuin.id]: foundersRuin,
  [theLow.id]: theLow,
  [wardingStones.id]: wardingStones,
  [deepWoodLimit.id]: deepWoodLimit,
  [undercroft.id]: undercroft,
  [hushHearth.id]: hushHearth,
};

export const startRoomId = hearthHall.id;

// MI1 framing: the view shows ~270 room-units tall; the renderer fits this HEIGHT to the window.
export const view = { w: 480, h: 270 };
export const scale = 4;

// The quest spine. `win` is the set of conditions that count as "game won"; `initialGlobal` seeds the
// starting saga flags. The Act III payoff is the WHY made plain in the undercroft (`why_known`), which
// the social climax and the treeline refusal lean on — so it stands as the world's win condition until
// the true climax-refusal flag lands.
export const quest: QuestSpec = {
  win: [{ key: "why_known", scope: "global" }],
  initialGlobal: {}, // The Long Kindling has no carried-flame spine in Act I (the watch-fire is at the edge)
};
